/**
 * Troubleshooting Reason vocabulary — the reasons an item can be sent to a
 * troubleshooting queue, their billing subtypes, and which reasons each target
 * stage offers.
 *
 * GP-1464 moved these out of SendToTroubleshootingDialog. The dialog still
 * re-exports them (its spec and older imports read them from there), but the
 * definitions live here so `constants/billingTroubleshootingSteps` can key its
 * workflow pathways off the same map WITHOUT importing the dialog — the dialog
 * now reads the pathways back, and a module cycle would have left
 * TROUBLESHOOTING_REASONS in its temporal dead zone at import time.
 */

// Common troubleshooting reasons by stage
export const TROUBLESHOOTING_REASONS: Record<string, string> = {
  // Billing/Insurance Issues
  'insurance_rejection': 'Insurance Rejection',
  'lu_code': 'LU Code Missing/Expired',
  'foc_approval_pending': 'FOC Approval Pending',
  'copay_outstanding': 'Copay Outstanding',
  'patient_payment_outstanding': 'Patient Payment Outstanding',
  'follow_up_with_patient': 'Follow Up With Patient',
  'refills_required': 'Refills Required',
  'missing_rx_image': 'Missing RX Image',
  'too_early_to_bill': 'Too Early to Bill',
  'patient_unreachable_billing': 'Patient is Unreachable',
  // GP-883: parked while delivery logistics are worked out with the patient.
  'pending_delivery_arrangement': 'Pending Delivery Arrangement',

  // GP-1176: Clinical/prescription-data corrections that route to Billing Queue
  // Troubleshooting. Previously these read like reason choices but were only
  // reachable as free text in the Additional Notes field — now first-class,
  // selectable Reason options for the billing target stage.
  'wrong_drug': 'Wrong Drug',
  'wrong_strength': 'Wrong Strength',
  'wrong_doctor': 'Wrong Doctor',
  'wrong_md_address': 'Wrong MD Address/Location',
  'wrong_sig': 'Wrong SIG',
  'wrong_qty': 'Wrong Qty',
  'wrong_days_supply': 'Wrong Days Supply',
  'wrong_copay': 'Wrong Copay',
  'adjudication_issue': 'Adjudication Issue',
  'wrong_refills': 'Wrong Refills',

  // Clinical Issues
  'drug_interaction': 'Drug Interaction Concern',
  'dosage_clarification': 'Dosage Clarification Needed',
  'allergy_concern': 'Allergy Concern',
  'clinical_review': 'Clinical Review Required',
  'prescriber_contact': 'Need to Contact Prescriber',

  // Inventory Issues
  'out_of_stock': 'Medication Out of Stock',
  'partial_fill': 'Partial Fill Required',
  'manufacturer_backorder': 'Manufacturer Backorder',
  'expired_medication': 'Expired Medication',

  // Patient Issues
  'patient_unreachable': 'Patient Unreachable',
  'address_verification': 'Address Verification Needed',
  'patient_request_hold': 'Patient Requested Hold',
  'patient_hospitalized': 'Patient Hospitalized',

  // Scheduling Troubleshooting Reasons (GP-275)
  'billing_brand_generic_cost': 'Billing issue - Brand vs. generic cost discrepancy',
  'billing_financial_assistance_psp': 'Billing issue - Financial Assistance pending from PSP',
  'billing_lu_code_missing': 'Billing issue - LU code missing',
  'billing_additional_coverage': 'Billing issue - Patient has additional coverage',
  'billing_sa_eap_expired': 'Billing issue - SA/EAP expired',
  'billing_too_early_duplicate': 'Billing issue - Too early to bill / duplicate claim',
  'billing_trillium_not_active': 'Billing issue - Trillium not active',
  'change_address_patient_request': 'Change in address (requested by patient)',
  'change_date_patient_request': 'Change in delivery date (requested by patient)',
  'clinical_awaiting_treatment': 'Clinical issue - Awaiting treatment clarification from prescriber',
  'clinical_pending_clearance': 'Clinical issue - Pending medical clearance',
  'foc_stock_issue': 'FOC stock issue (FOC supply not received)',
  'incorrect_delivery_address': 'Incorrect delivery address',
  'inventory_stock_shortage': 'Inventory Issue (Stock shortage)',
  'outstanding_payment': 'Outstanding payment (patient balance unpaid)',
  'patient_has_medication_on_hand': 'Patient has more than a month of medication on hand',
  'patient_treatment_on_hold': 'Patient Treatment on Hold',
  'patient_unreachable_pre_delivery': 'Patient unreachable (pre-delivery)',
  'therapy_changed': 'Therapy changed (patient switched to another medication)',

  // Packaging/Verification Issues
  'packaging_error': 'Packaging Error',
  'label_issue': 'Label Printing Issue',
  'barcode_mismatch': 'Barcode Mismatch',
  'quantity_discrepancy': 'Quantity Discrepancy',
  // GP-460: Awaiting patient payment — routes order to a holding state
  // while waiting for the patient to complete payment before shipment.
  'awaiting_patient_payment': 'Awaiting Patient Payment',

  // Delivery Issues
  'delivery_failed': 'Delivery Failed',
  'wrong_address': 'Wrong Delivery Address',
  'driver_issue': 'Driver Issue',
  'weather_delay': 'Weather Delay',
  'missed_pickup': 'Missed Pickup - Please Reschedule',
  'unconfirmed_delivery': 'Unconfirmed Delivery',

  // Stage Management
  'manual_stage_change': 'Manual Stage Change',

  // Other
  'other': 'Other (see notes)',
};

// GP-326: Billing T/S reason subtypes (cascading dropdown)
// Exported so the billing playbooks (constants/billingTroubleshootingSteps.ts) can
// spec-pin their subtype labels against THIS map — a rename here must fail that
// spec, not silently fall a subtype back to the primary pathway (GP-368 review).
export const BILLING_REASON_SUBTYPES: Record<string, string[]> = {
  insurance_rejection: [
    'Plan/Quantity Max Exceeded',
    'ODB Social Assistance Issue',
    'Electronic Trillium Not going through',
    'Trillium not in place',
    'SA Expired/EAP expired',
    'Coverage Terminated',
    'Billing too early/Duplicate Claim',
    'Mandatory Biosimilar',
    'Patient has other coverage',
  ],
  foc_approval_pending: [
    'FOC through copay card',
    'FOC through stock sent by manufacturer',
    'FOC through another specialty pharmacy',
  ],
  follow_up_with_patient: [
    'Treatment is on hold',
    'Patient is unreachable',
    'Patient is on vacation',
  ],
};

// Reasons offered per TARGET stage. Module-scope (no component state) and
// exported so the per-stage lists are spec-pinned (troubleshootingReasons.spec.ts).
export const getReasonsByTargetStage = (stage: string): string[] => {
  const stageReasons: Record<string, string[]> = {
    billing: [
      'insurance_rejection',
      'lu_code',
      'foc_approval_pending',
      'copay_outstanding',
      'patient_payment_outstanding',
      'follow_up_with_patient',
      'refills_required',
      'missing_rx_image',
      'too_early_to_bill',
      'patient_unreachable_billing',
      'pending_delivery_arrangement', // GP-883
      // GP-1176: clinical/prescription-data corrections. Keyed by TARGET stage,
      // so any source stage (Clinical Verification, Packaging, etc.) that routes
      // an order to Billing Queue Troubleshooting gets these Reason options too.
      'wrong_drug',
      'wrong_strength',
      'wrong_doctor',
      'wrong_md_address',
      'wrong_sig',
      'wrong_qty',
      'wrong_days_supply',
      'wrong_copay',
      'adjudication_issue',
      'wrong_refills',
    ],
    clinic_scheduling: [
      'billing_brand_generic_cost',
      'billing_financial_assistance_psp',
      'billing_lu_code_missing',
      'billing_additional_coverage',
      'billing_sa_eap_expired',
      'billing_too_early_duplicate',
      'billing_trillium_not_active',
      'change_address_patient_request',
      'change_date_patient_request',
      'clinical_awaiting_treatment',
      'clinical_pending_clearance',
      'foc_stock_issue',
      'incorrect_delivery_address',
      'inventory_stock_shortage',
      'outstanding_payment',
      'patient_has_medication_on_hand',
      'patient_treatment_on_hold',
      'patient_unreachable_pre_delivery',
      'therapy_changed',
    ],
    clinical_verification: [
      'drug_interaction',
      'dosage_clarification',
      'allergy_concern',
      'clinical_review',
      'prescriber_contact',
      // GP-1055 epic: prescription-data corrections and patient-contact reasons,
      // mirroring the GP-1176 Billing expansion where clinically relevant.
      // Purely financial reasons stay on the billing target list above.
      'wrong_drug',
      'wrong_strength',
      'wrong_doctor',
      'wrong_md_address',
      'wrong_sig',
      'wrong_qty',
      'wrong_days_supply',
      'wrong_refills',
      'missing_rx_image',
      'refills_required',
      'follow_up_with_patient',
      'patient_unreachable',
    ],
    packaging: [
      'out_of_stock',
      'partial_fill',
      'manufacturer_backorder',
      'packaging_error',
      'label_issue',
    ],
    product_verification: [
      'barcode_mismatch',
      'quantity_discrepancy',
      'packaging_error',
      'expired_medication',
      'awaiting_patient_payment', // GP-460
    ],
  };

  return stageReasons[stage] || Object.keys(TROUBLESHOOTING_REASONS);
};
